/**
 * Gathers the project context Task Intelligence needs: existing open tasks
 * (for duplicate/update detection), org members (assignee resolution), and
 * locations — the same sources the manual Task Editor already uses, so a
 * recommendation can only ever point at something a human could also pick.
 */
import { supabase } from "../supabase";
import { CAPABILITY } from "../helpers/permissions";
import { loadScopedUsers } from "../helpers/scopedQueries";
import { loadProjectTasks } from "../helpers/tasks/projectTasks";
import { TASK_STATUS } from "../helpers/tasks/taskModel";

export function todayIsoDate(now = new Date()) {
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${m}-${d}`;
}

/**
 * @returns {Promise<{ existingTasks: object[], members: object[], locations: object[] }>}
 */
export async function gatherTaskIntelligenceContext(actor, { projectId, locations = [], directReportIds } = {}) {
  const [{ tasks }, people] = await Promise.all([
    loadProjectTasks(actor, projectId),
    loadScopedUsers(supabase, actor, CAPABILITY.TIMELOG_VIEW, "supabase_user_id, employee_name", {
      directReportIds,
    }).catch(() => []),
  ]);

  const nameByUserId = new Map((people || []).map((p) => [p.supabase_user_id, p.employee_name]));

  const existingTasks = (tasks || [])
    .filter((t) => t.status !== TASK_STATUS.COMPLETED)
    .map((t) => ({
      id: t.project_task_id,
      title: t.title,
      status: t.status,
      assigneeName: t.assignee_id ? nameByUserId.get(t.assignee_id) || null : null,
      dueDate: t.due_date || null,
    }));

  const members = (people || []).map((p) => ({ id: p.supabase_user_id, name: p.employee_name || "" }));
  const locationContext = (locations || []).map((l) => ({ id: l.project_location_id, name: l.name }));

  return { existingTasks, members, locations: locationContext };
}
